import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { Button } from "./Button";
import { Dialog } from "./Dialog";

interface ConfirmRequest {
	title: string;
	description: string;
	confirmLabel?: string;
	onConfirm: () => void;
}

const ConfirmContext = createContext<((request: ConfirmRequest) => void) | null>(null);

export const useConfirm = (): ((request: ConfirmRequest) => void) => {
	const confirm = useContext(ConfirmContext);
	if (!confirm) throw new Error("useConfirm must be used inside ConfirmProvider");
	return confirm;
};

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
	const [request, setRequest] = useState<ConfirmRequest | null>(null);

	const confirm = useCallback((next: ConfirmRequest) => setRequest(next), []);

	const accept = () => {
		request?.onConfirm();
		setRequest(null);
	};

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}
			<Dialog
				open={request !== null}
				onOpenChange={(open) => !open && setRequest(null)}
				title={request?.title ?? ""}
				description={request?.description}
				footer={
					<>
						<Button variant="outline" onClick={() => setRequest(null)}>
							Cancel
						</Button>
						<Button onClick={accept}>{request?.confirmLabel ?? "Delete"}</Button>
					</>
				}
			>
				<p className="text-xs text-ink-muted">This cannot be undone.</p>
			</Dialog>
		</ConfirmContext.Provider>
	);
};
